import {
  SCHEMA_LOADED,
  MODAL_INTERACTION,
  TABLE_COLUMNS_LOADED,
  SCHEMA_SAVED,
  LOADING,
  CHANGE_TAB,
  MAP_ENABLED,
} from '../actions/constants';
import TableRef from '../util/TableRef';
import TovBuilder from '../util/TovBuilder';
import ColumnRef from '../util/ColumnRef';
import Column from '../util/Column';
import { MODAL_ER_DIAGRAM } from '../util/enums/EnumModals';
import ErLink from '../util/ErLink';

const INITIAL_STATE = {
  loaded: false,
  from: null, // list of ColumnRef objs
  to: null, // list of ColumnRef objs
  enabledTables: [], // list of TableRef objs
  enabledLinks: [], // list of ErLink objs
  pks: null, // list of ColumnRef objs

  selectedTable: null, // TableRef or null

  selectedFkTable: null, // TableRef or null
  fkColumns: null, // null or list of Column objs
  selectedFkColumn: null, // Column or null
  localColumns: null, // null or list of Column objs
  selectedLocalColumn: null, // Column or null

  positions: {}, // uniqueName -> [x, y]
  colours: {}, // uniqueName -> colour [blue/red/yellow/purple/white]
  descriptions: {}, // uniqueName -> string

  enablingMap: false, // true if user has clicked to enable map
  needsSaving: false, // trigger listens to this, waiting to save
};

function buildEnabledLinks(newState) {
  const lookup = {};
  newState.enabledTables.forEach((t) => {
    lookup[t.uniqueName()] = true;
  });
  const { from } = newState;
  const { to } = newState;
  const results = [];
  for (let i = from.length - 1; i >= 0; i--) {
    if (
      lookup[from[i].tov().uniqueName()] &&
      lookup[to[i].tov().uniqueName()]
    ) {
      const isOneToMany =
        newState.pks.findIndex((pk) => pk.equals(from[i])) !== -1;
      results.push(new ErLink(from[i], to[i], isOneToMany));
    }
  }
  return results;
}

function removeIndex(array, index) {
  return [...array.slice(0, index), ...array.slice(index + 1)];
}

function interaction(state, payload) {
  switch (payload.section) {
    case 'toggleTable': {
      const isBeingRemoved =
        state.enabledTables.findIndex((t) => t.equals(payload.table)) !== -1;
      const enabledTables = isBeingRemoved
        ? state.enabledTables.filter((t) => !t.equals(payload.table)) // remove from list
        : [...state.enabledTables, payload.table]; // add to list
      const positions = { ...state.positions };
      if (isBeingRemoved) {
        delete positions[payload.table.uniqueName()];
      }
      return {
        ...state,
        enabledTables,
        needsSaving: true,
        positions,
      };
    }
    case 'table-selected': {
      const tableRef = !payload.uniqueName
        ? null
        : state.enabledTables.find(
            (t) => t.uniqueName() === payload.uniqueName,
          );
      return {
        ...state,
        selectedTable: tableRef,
        selectedFkTable: null,
        localColumns: null,
      };
    }
    case 'remove-fk': {
      const fromToRemove = state.from[payload.index];
      const toToRemove = state.to[payload.index];
      // remove requested index
      const newFroms = removeIndex(state.from, payload.index);
      const newTos = removeIndex(state.to, payload.index);
      // find other side of relationship and delete that too
      const indexToRemove = newFroms.findIndex((from, i) => {
        const to = newTos[i];
        return to.equals(fromToRemove) && from.equals(toToRemove);
      });
      return {
        ...state,
        from: removeIndex(newFroms, indexToRemove),
        to: removeIndex(newTos, indexToRemove),
        needsSaving: true,
      };
    }
    case 'new-fk': {
      return {
        ...state,
        selectedFkTable: payload.fkTable,
        fkColumns: null, // reset
        selectedFkColumn: null, // reset
        selectedLocalColumn: null, // reset
      };
    }
    case 'fk-column': {
      return { ...state, selectedFkColumn: payload.column };
    }
    case 'local-column': {
      return { ...state, selectedLocalColumn: payload.column };
    }
    case 'add-fk': {
      const a = new ColumnRef(
        state.selectedTable,
        state.selectedLocalColumn.name().name(),
      );
      const b = new ColumnRef(
        state.selectedFkTable,
        state.selectedFkColumn.name().name(),
      );
      return {
        ...state,
        from: [...state.from, a, b],
        to: [...state.to, b, a],
        selectedFkTable: null, // reset
        needsSaving: true,
      };
    }
    case 'position-updated': {
      const existing = state.positions[payload.uniqueName];
      if (existing && existing[0] === payload.x && existing[1] === payload.y) {
        // nothing changed
        return state;
      }
      const updates = {};
      updates[payload.uniqueName] = [payload.x, payload.y];
      return {
        ...state,
        positions: { ...state.positions, ...updates },
        needsSaving: true,
      };
    }
    case 'change-colour': {
      const existing = state.colours[payload.table.uniqueName()];
      if (existing && existing === payload.colour) {
        // nothing changed
        return state;
      }
      const updates = {};
      updates[payload.table.uniqueName()] = payload.colour;
      return {
        ...state,
        colours: { ...state.colours, ...updates },
        needsSaving: true,
      };
    }
    case 'description': {
      const updates = {};
      updates[payload.table.uniqueName()] = payload.newValue;
      return {
        ...state,
        descriptions: { ...state.descriptions, ...updates },
        needsSaving: true,
      };
    }
    default:
      return state;
  }
}

const ErDiagram = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case CHANGE_TAB: {
      // reset state (don't want to take up too much memory unnecessarily)
      return { ...INITIAL_STATE };
    }
    case LOADING: {
      if (action.awaiting === SCHEMA_SAVED) {
        return {
          ...state,
          needsSaving: false, // reset
        };
      }
      if (action.awaiting === MAP_ENABLED) {
        return { ...state, enablingMap: true };
      }
      return state;
    }
    case SCHEMA_LOADED: {
      const positions = {};
      const colours = {};
      const descriptions = {};
      action.payload.schema._tables.map((ts) => {
        const tabRef = TableRef.buildFromServer(ts._table);
        if (ts._x) {
          positions[tabRef.uniqueName()] = [ts._x, ts._y];
        }
        if (ts._colour) {
          colours[tabRef.uniqueName()] = ts._colour;
        }
        if (ts._description) {
          descriptions[tabRef.uniqueName()] = ts._description;
        }
      });
      const pks = action.payload.schema._pks.map(
        (pk) => new ColumnRef(TovBuilder.build(pk._table), pk._name),
      );
      const newState = {
        ...state,
        loaded: true,
        from: action.payload.schema._fks.map(
          (fk) =>
            new ColumnRef(TovBuilder.build(fk._from._table), fk._from._name),
        ),
        to: action.payload.schema._fks.map(
          (fk) => new ColumnRef(TovBuilder.build(fk._to._table), fk._to._name),
        ),
        enabledTables: action.payload.schema._tables
          .map((ts) => TableRef.buildFromServer(ts._table))
          .filter((table) =>
            action.payload.allTables.find((t) => t.equals(table)),
          ), // only include tables that haven't been hidden
        positions,
        colours,
        descriptions,
        pks,
      };
      return { ...newState, enabledLinks: buildEnabledLinks(newState) };
    }
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== MODAL_ER_DIAGRAM) {
        return state;
      }
      const newState = interaction(state, action.payload.props);
      return { ...newState, enabledLinks: buildEnabledLinks(newState) };
    }
    case TABLE_COLUMNS_LOADED: {
      const columns = action.payload.json.map((c) => Column.build(c));
      const fkColumns = action.payload.table.equals(state.selectedFkTable)
        ? columns
        : state.fkColumns;
      const localColumns = action.payload.table.equals(state.selectedTable)
        ? columns
        : state.localColumns;
      return { ...state, fkColumns, localColumns };
    }
    default:
      return state;
  }
};

export default ErDiagram;
